import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ActionNeededIndicator } from '../../../components/ActionNeededIndicator';
import CopyableText from '../../../components/CopyableText';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell, Status } from '../../../components/StateCell';
import { StyledHeader } from '../../../components/Table/Header';
import { InstrumentsById } from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import { fmt, formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import { Direction } from './Direction';
import { Row } from './types';

export const createHeaders: (instrumentsById: InstrumentsById) => GridColDef<Row>[] = (
  instrumentsById,
) => {
  return [
    {
      field: 'state',
      headerName: 'State',
      renderHeader: () => (
        <StyledHeader sx={{ ml: 2.5 }} variant="caption">
          State
        </StyledHeader>
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <ActionNeededIndicator
            isOffered={params.row.state === 'Offered' || params.row.state === 'Offered2step'}
            receiver={params.row.payload.receiver}
          />

          <StateCell reason={params.row.reason} state={params.row.state as Status} />
        </Box>
      ),
      flex: 80,
      valueGetter: (params) => params.row.state,
      filterable: true,
      minWidth: 100,
    },
    {
      field: 'sender',
      headerName: 'Sender',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Direction sender={params.row.payload.sender} receiver={params.row.payload.receiver} />
          <PartyDisplay party={params.row.payload.sender} />
        </Box>
      ),
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.sender,
      minWidth: 100,
    },
    {
      field: 'receiver',
      headerName: 'Receiver',
      renderCell: (params) => <PartyDisplay party={params.row.payload.receiver} />,
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.receiver,
      minWidth: 100,
    },
    {
      field: 'instrumentId',
      headerName: 'Instrument Id',
      valueGetter: (params) => params.row.payload.instrumentIdentifier.id,
      renderCell: (params) => <CopyableText value={params.row.payload.instrumentIdentifier.id} />,
      filterable: true,
      minWidth: 100,
      flex: 100,
    },
    {
      field: 'tokenName',
      headerName: 'Token Name',
      valueGetter: (params) =>
        instrumentsById[params.row.payload.instrumentIdentifier.id]?.payload.tokenName || '',
      filterable: true,
      minWidth: 100,
      flex: 100,
    },
    {
      field: 'tokenSymbol',
      headerName: 'Token Symbol',
      valueGetter: (params) =>
        instrumentsById[params.row.payload.instrumentIdentifier.id]?.payload.tokenSymbol || '',
      filterable: true,
      minWidth: 100,
      flex: 100,
    },
    {
      field: 'cusip',
      headerName: 'CUSIP',
      valueGetter: (params) =>
        instrumentsById[params.row.payload.instrumentIdentifier.id]?.payload.cusip || '',
      filterable: true,
      minWidth: 100,
      flex: 100,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      renderCell: (params) => fmt(params.row.payload.amount, 2),
      valueGetter: (params) => Number(params.row.payload.amount),
      filterable: false,
      minWidth: 70,
    },
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.registrar,
      minWidth: 100,
    },
    {
      field: 'executeBefore',
      headerName: 'Execute Before',
      minWidth: 180,
      type: 'dateTime',
      valueGetter: (params) => parseTimestampToDate(params.row.payload.executeBefore),
      renderCell: (params) => formatTimestamp(params.row.payload.executeBefore),
    },
    {
      field: 'createdAt',
      headerName: 'Timestamp',
      minWidth: 180,
      type: 'dateTime',
      valueGetter: (params) => parseTimestampToDate(params.row.createdAt),
      renderCell: (params) => formatTimestamp(params.row.createdAt),
    },
    {
      field: 'reference',
      headerName: 'Reference',
      valueGetter: (params) => params.row.payload.reference,
      flex: 150,
      filterable: true,
      minWidth: 100,
    },
  ];
};
